import { registerLocaleData } from '@angular/common';
import { provideHttpClient } from '@angular/common/http';
import localeFr from '@angular/common/locales/fr';
import { ApplicationConfig, ErrorHandler, inject, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Router, withComponentInputBinding } from '@angular/router';

import { environment } from '@epi/environments';
import { definePreset } from '@primeng/themes';
import Aura from '@primeuix/themes/aura';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';
import { providePrimeNG } from 'primeng/config';

import { appRoutes } from './app.routes';
import { FIREBASE_CONFIG } from './firebase.config';
import { DATE_PROVIDER, RealDateProvider } from './helpers/date.provider';
import { RealUidProvider, UID_PROVIDER } from './helpers/uid.provider';

registerLocaleData(localeFr, 'fr');

if (environment.sentry) {
  Sentry.init({
    dsn: (environment.sentry as { dsn: string }).dsn,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const ApplicationPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideFirebaseApp(() => initializeApp(FIREBASE_CONFIG)),
    provideAuth(() => getAuth()),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
    provideFunctions(() => getFunctions(getApp(), 'europe-west1')),
    /**
     * Still needed for PrimeNg (dialogs, etc.)
     */
    provideAnimationsAsync(),
    provideHttpClient(),
    provideRouter(appRoutes, withComponentInputBinding()),
    providePrimeNG({
      theme: {
        preset: ApplicationPreset,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    {
      provide: LOCALE_ID,
      useValue: 'fr-FR',
    },
    { provide: UID_PROVIDER, useClass: RealUidProvider },
    { provide: DATE_PROVIDER, useClass: RealDateProvider },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: (permissionsGateway: PermissionsGateway) => () => permissionsGateway.initializeRoles(),
    //   deps: [PermissionsGateway],
    //   multi: true,
    // },
  ],
};
